import {
  Box,
  Flex,
  Icon,
  IconButton,
  Text,
} from '@chakra-ui/react'
import {
  Download,
  File,
  FileSpreadsheet,
  FileText,
  Image,
  Trash2
} from 'lucide-react'
import React, { useState } from 'react'
import { apiClient } from '../../api'
import { UploadedAsset } from '../../types/chatTypes'

interface DocumentAttachmentProps {
  document: UploadedAsset
  onRemove?: () => void
  isRemoving?: boolean
  variant?: 'message' | 'input' // New variant for different use cases
}

// Function to get document icon and styling based on content type
function getDocumentDisplay(contentType: string, filename: string) {
  const lowerFilename = filename.toLowerCase();

  if (contentType.includes('pdf') || lowerFilename.endsWith('.pdf')) {
    return {
      icon: FileText,
      iconColor: 'red.500',
      borderColor: 'red.200',
      type: 'PDF'
    };
  }

  if (contentType.includes('sheet') || contentType.includes('excel') ||
      lowerFilename.endsWith('.xlsx') || lowerFilename.endsWith('.xls')) {
    return {
      icon: FileSpreadsheet,
      iconColor: 'green.500',
      borderColor: 'green.200',
      type: 'Spreadsheet'
    };
  }

  if (lowerFilename.endsWith('.csv')) {
    return {
      icon: FileSpreadsheet,
      iconColor: 'green.500',
      borderColor: 'green.200',
      type: 'CSV'
    };
  }

  if (contentType.includes('word') || contentType.includes('document') ||
      lowerFilename.endsWith('.docx') || lowerFilename.endsWith('.doc')) {
    return {
      icon: FileText,
      iconColor: 'blue.500',
      borderColor: 'blue.200',
      type: 'Document'
    };
  }

  if (contentType.includes('image') ||
      lowerFilename.endsWith('.jpg') || lowerFilename.endsWith('.jpeg') ||
      lowerFilename.endsWith('.png') || lowerFilename.endsWith('.webp')) {
    return {
      icon: Image,
      iconColor: 'purple.500',
      borderColor: 'purple.200',
      type: 'Image'
    };
  }

  // Default for unknown types
  return {
    icon: File,
    iconColor: 'gray.500',
    borderColor: 'gray.200',
    type: 'File'
  };
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export const DocumentAttachment: React.FC<DocumentAttachmentProps> = ({
  document,
  onRemove,
  isRemoving = false,
  variant = 'input',
}) => {
  const documentDisplay = getDocumentDisplay(document.content_type, document.filename);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await apiClient.document.downloadDocument(document.id);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.filename;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // axiosClient interceptor already surfaces the error toast.
    } finally {
      setIsDownloading(false);
    }
  };

  // Minimalistic message variant
  if (variant === 'message') {
    return (
      <Box
        p={2}
        border="1px"
        borderColor={documentDisplay.borderColor}
        borderRadius="md"
        bg="white"
        _hover={{
          borderColor: documentDisplay.iconColor,
        }}
        transition="all 0.2s"
      >
        <Flex alignItems="center" gap={2}>
          <Icon
            as={documentDisplay.icon}
            boxSize={4}
            color={documentDisplay.iconColor}
          />
          <Box flex="1" minW="0">
            <Text
              fontWeight="medium"
              color="gray.700"
              noOfLines={1}
              fontSize="sm"
            >
              {document.filename}
            </Text>
            <Text
              fontSize="xs"
              color="gray.500"
            >
              {documentDisplay.type} • {formatFileSize(document.file_size)}
            </Text>
          </Box>
          <IconButton
            aria-label="Download document"
            icon={<Download size={14} />}
            size="xs"
            variant="ghost"
            onClick={handleDownload}
            isLoading={isDownloading}
            color={documentDisplay.iconColor}
            _hover={{
              bg: 'gray.100',
            }}
          />
        </Flex>
      </Box>
    );
  }

  // Original input variant (existing functionality)
  return (
    <Box
      bg="gray.50"
      border="1px"
      borderColor="gray.200"
      borderRadius="md"
      p={3}
      mb={2}
      transition="all 0.2s"
      _hover={{
        borderColor: 'primary',
        boxShadow: 'sm',
      }}
    >
      <Flex align="center" justify="space-between">
        <Flex align="center" flex="1" minW="0">
          <Icon
            as={documentDisplay.icon}
            color={documentDisplay.iconColor}
            boxSize={5}
            mr={3}
            flexShrink={0}
          />
          <Box flex="1" minW="0">
            <Text
              fontSize="sm"
              fontWeight="medium"
              color="gray.700"
              noOfLines={1}
              title={document.filename}
            >
              {document.filename}
            </Text>
            <Text
              fontSize="xs"
              color="gray.500"
              mt={1}
            >
              {formatFileSize(document.file_size)} • Uploaded {new Date(document.created_at).toLocaleDateString()}
            </Text>
          </Box>
        </Flex>

        {onRemove && (
          <IconButton
            aria-label="Remove document"
            icon={<Icon as={Trash2} />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            ml={2}
            flexShrink={0}
            onClick={onRemove}
            isDisabled={isRemoving}
            _hover={{
              bg: 'red.50',
            }}
          />
        )}
      </Flex>
    </Box>
  );
}
